// apps/web/src/routes/(showcase)/showcase/index.tsx
import { Tooltip, TooltipContent, TooltipTrigger } from "@acme/ui/tooltip";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  BarChart3,
  CalendarDays,
  Camera,
  Info,
  Layers,
  type LucideIcon,
  MapPin,
  Palette,
  PlayCircle,
  ScrollText,
  Sparkles,
  Star,
} from "lucide-react";
import { AnimatedCounter } from "~/components/showcase/motion/animated-counter";
import { ScrollReveal } from "~/components/showcase/motion/scroll-reveal";
import { GoldRule } from "~/components/showcase/ornaments/gold-rule";

import { ReviewsSummary } from "~/components/showcase/reviews/reviews-summary";
import { seo } from "~/lib/seo";
import { useSkin } from "~/lib/showcase/skin";
import { useTour } from "~/lib/showcase/tour-context";
import {
  generateStructuredDataGraph,
  getShowcasePageSchemas,
} from "~/lib/structured-data";
import * as m from "~/paraglide/messages.js";

export const Route = createFileRoute("/(showcase)/showcase/")({
  component: ShowcaseIndex,
  head: () => {
    const title = m.meta_showcase_index_title();
    const description = m.meta_showcase_index_description();
    const seoData = seo({
      // The hospitality booking demo no longer matches what Lindevo sells.
      // Kept working and crawlable, but out of the index until it is ported
      // out to apps/mockup. Also removed from sitemap.xml.
      noindex: true,
      description,
      path: "/showcase",
      title,
    });
    const structuredData = generateStructuredDataGraph(
      getShowcasePageSchemas({
        description,
        isIndex: true,
        pageName: title,
        path: "/showcase",
      })
    );
    return {
      links: seoData.links,
      meta: seoData.meta,
      scripts: [{ children: structuredData, type: "application/ld+json" }],
    };
  },
});

const HERO_PHOTOS = [
  {
    alt: "Boutique hotel lobby with warm lighting",
    src: "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=900&auto=format&fit=crop&q=70",
  },
  {
    alt: "Modern hotel bedroom",
    src: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=900&auto=format&fit=crop&q=70",
  },
  {
    alt: "Elegant suite with city view",
    src: "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=900&auto=format&fit=crop&q=70",
  },
] as const;

const MODERN_HERO =
  "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=2000&auto=format&fit=crop&q=80";
const HERITAGE_HERO =
  "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=1400&auto=format&fit=crop&q=80";

function ShowcaseIndex() {
  const { skin } = useSkin();
  return (
    <div>
      {skin === "heritage" ? <HeritageHero /> : <ModernHero />}
      <ScrollReveal>
        <PhotoStrip />
      </ScrollReveal>
      <ScrollReveal delay={0.05}>
        <StorylineSection />
      </ScrollReveal>
      <ScrollReveal delay={0.05}>
        <ReviewsTeaser />
      </ScrollReveal>
      <ScrollReveal delay={0.05}>
        <WhySection />
      </ScrollReveal>
      <ScrollReveal delay={0.05}>
        <ExploreSection />
      </ScrollReveal>
    </div>
  );
}

/* ───────────────────────────── MODERN HERO ───────────────────────────── */

function ModernHero() {
  return (
    <section className="relative">
      <div className="relative h-[78vh] min-h-[560px] w-full overflow-hidden">
        <img
          alt="Hotel Aero suite at dusk"
          className="sc-photo h-full w-full object-cover"
          fetchPriority="high"
          height={1200}
          src={MODERN_HERO}
          width={2000}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.10) 30%, rgba(0,0,0,0.55) 60%, rgba(0,0,0,0.92) 100%)",
          }}
        />
        <div className="absolute inset-x-0 bottom-0">
          <div className="mx-auto flex max-w-6xl flex-col gap-6 px-6 pb-16 sm:pb-20">
            <p
              className="sc-eyebrow flex items-center gap-3"
              style={{
                color: "rgba(255,255,255,0.95)",
                textShadow: "0 1px 12px rgba(0,0,0,0.55)",
              }}
            >
              <span aria-hidden="true" className="h-px w-10 bg-white/70" />
              {m.showcase_index_eyebrow()}
            </p>
            <h1
              className="sc-display max-w-4xl text-balance font-medium text-5xl text-white leading-[0.95] sm:text-7xl"
              style={{
                letterSpacing: "var(--sc-tracking-display)",
                textShadow: "0 2px 24px rgba(0,0,0,0.4)",
              }}
            >
              {m.showcase_index_headline()}
            </h1>
            <p
              className="max-w-2xl text-base text-white/95 sm:text-lg"
              style={{ textShadow: "0 1px 12px rgba(0,0,0,0.45)" }}
            >
              {m.showcase_index_lead()}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <LandingTourCTA accent="invert" />
              <div className="flex flex-wrap items-center gap-3">
                <Link
                  className="sc-btn inline-flex items-center gap-2 border border-white/30 bg-white/10 px-4 py-3 text-sm text-white backdrop-blur transition hover:bg-white/20"
                  to="/showcase/booking"
                >
                  <CalendarDays aria-hidden="true" className="size-4" />
                  {m.showcase_cta_try_calendar()}
                </Link>
                <Link
                  className="sc-btn inline-flex items-center gap-2 border border-white/30 bg-white/10 px-4 py-3 text-sm text-white backdrop-blur transition hover:bg-white/20"
                  to="/showcase/concierge"
                >
                  <Sparkles aria-hidden="true" className="size-4" />
                  {m.showcase_cta_try_concierge()}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Operator snapshot — an editorial spec ledger. The top/bottom hairlines run
          full-bleed (echoing the full-width hero above); the four columns stay
          centered on the page measure. Interior 1px gaps draw the column dividers
          (and the row divider in the mobile 2×2). */}
      <div className="border-[var(--sc-border)] border-y">
        <dl className="mx-auto grid max-w-6xl grid-cols-2 gap-px bg-[var(--sc-rule)] sm:grid-cols-4">
          <Kpi label={m.showcase_index_kpi_rooms()} to={12} />
          <Kpi
            explainer={m.showcase_kpi_adr_explainer()}
            label={m.showcase_index_kpi_adr()}
            prefix="€"
            to={189}
          />
          <Kpi label={m.showcase_index_kpi_occupancy()} suffix="%" to={94} />
          <Kpi
            decimals={1}
            label={m.showcase_index_kpi_rating()}
            suffix="★"
            to={4.8}
          />
        </dl>
      </div>
    </section>
  );
}

interface KpiProps {
  decimals?: number;
  explainer?: string;
  label: string;
  prefix?: string;
  suffix?: string;
  to: number;
}

function Kpi({
  decimals = 0,
  explainer,
  label,
  prefix = "",
  suffix = "",
  to,
}: KpiProps) {
  const format = (n: number) =>
    n.toLocaleString(undefined, {
      maximumFractionDigits: decimals,
      minimumFractionDigits: decimals,
    });
  // flex-col-reverse keeps valid <dt>-then-<dd> source order while showing the
  // figure above its label. Units (€ % ★) render as accent siblings — the one
  // functional signal — so the numeral itself stays ink.
  return (
    <div className="flex flex-col-reverse gap-3 bg-[var(--sc-bg)] px-5 py-8 sm:px-8 sm:py-10">
      <dt className="sc-eyebrow flex items-center gap-1">
        {label}
        {explainer ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                aria-label={explainer}
                className="inline-flex items-center text-[var(--sc-muted)] hover:text-[var(--sc-fg)]"
                type="button"
              >
                <Info aria-hidden="true" className="size-3" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-[220px] bg-[var(--sc-fg)] text-[var(--sc-bg)]">
              {explainer}
            </TooltipContent>
          </Tooltip>
        ) : null}
      </dt>
      <dd className="sc-display sc-num-display flex items-baseline gap-0.5 text-4xl text-[var(--sc-fg)] leading-none sm:text-5xl">
        {prefix ? (
          <span className="text-2xl text-[var(--sc-accent)] sm:text-3xl">
            {prefix}
          </span>
        ) : null}
        <AnimatedCounter className="sc-num-display" format={format} to={to} />
        {suffix ? (
          <span className="text-2xl text-[var(--sc-accent)] sm:text-3xl">
            {suffix}
          </span>
        ) : null}
      </dd>
    </div>
  );
}

/* ───────────────────────────── HERITAGE HERO ───────────────────────────── */

function HeritageHero() {
  // Split the headline at its first comma so the opening clause sets upright
  // (roman 700) and the lyrical tail sets in true italic (400). Both weights
  // are loaded; the prior uniform italic-700 was faux-synthesized. Splitting at
  // render time keeps the single shared i18n key (no new-key dev restart) and
  // works for EN "Two experiences," / LV "Divas pieredzes,".
  const headline = m.showcase_index_headline();
  const commaAt = headline.indexOf(",");
  const headlineLead = commaAt >= 0 ? headline.slice(0, commaAt + 1) : headline;
  const headlineTail = commaAt >= 0 ? headline.slice(commaAt + 1).trim() : "";
  return (
    <section className="relative">
      <div className="mx-auto max-w-6xl px-6 pt-12 sm:pt-20">
        <div className="grid gap-10 sm:grid-cols-12 sm:gap-12">
          <div className="sm:col-span-7">
            <p className="sc-eyebrow flex items-center gap-3">
              <span
                aria-hidden="true"
                className="h-px w-10"
                style={{ backgroundColor: "var(--sc-rule)" }}
              />
              EST · MCMXXIII · LIEPĀJA
            </p>
            <h1
              className="sc-display mt-6 text-[clamp(3rem,8vw,5.25rem)] text-[var(--sc-fg)] leading-[0.96]"
              style={{ letterSpacing: "var(--sc-tracking-display)" }}
            >
              <span className="block text-balance font-bold">
                {headlineLead}
              </span>
              {headlineTail ? (
                <span className="block text-balance font-normal italic">
                  {headlineTail}
                </span>
              ) : null}
            </h1>
            <GoldRule className="mt-6 max-w-md" variant="diamond" />
            <div className="mt-6 max-w-xl">
              <p className="sc-body text-pretty text-[var(--sc-fg)] text-xl leading-relaxed">
                {m.showcase_index_lead()}
              </p>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <LandingTourCTA accent="solid" />
              <div className="flex flex-wrap items-center gap-3">
                <Link
                  className="sc-btn inline-flex items-center gap-2 border bg-[var(--sc-surface)] px-4 py-3 text-[var(--sc-fg)] text-sm hover:bg-[var(--sc-surface-2)]"
                  style={{ borderColor: "var(--sc-rule)" }}
                  to="/showcase/booking"
                >
                  <CalendarDays aria-hidden="true" className="size-4" />
                  {m.showcase_cta_try_calendar()}
                </Link>
                <Link
                  className="sc-btn inline-flex items-center gap-2 border bg-[var(--sc-surface)] px-4 py-3 text-[var(--sc-fg)] text-sm hover:bg-[var(--sc-surface-2)]"
                  style={{ borderColor: "var(--sc-rule)" }}
                  to="/showcase/concierge"
                >
                  <Sparkles aria-hidden="true" className="size-4" />
                  {m.showcase_cta_try_concierge()}
                </Link>
              </div>
            </div>
          </div>

          <div className="sm:col-span-5">
            <figure
              className="relative overflow-hidden border bg-[var(--sc-surface)] p-3"
              style={{
                borderColor: "var(--sc-rule)",
                borderRadius: "var(--sc-radius)",
                boxShadow: "var(--sc-shadow)",
              }}
            >
              <img
                alt="Hotel Aero portrait"
                className="sc-photo aspect-[3/4] w-full object-cover"
                decoding="async"
                height={1200}
                src={HERITAGE_HERO}
                width={900}
              />
              <figcaption className="mt-3 px-1 pb-1 font-[family-name:var(--sc-font-body)] text-[var(--sc-muted)] text-sm italic">
                {m.showcase_index_eyebrow()}
              </figcaption>
            </figure>
          </div>
        </div>

        <GoldRule className="mt-16" variant="thin" />
        <div className="grid grid-cols-3 gap-8 py-8 text-center">
          <Plate
            label={m.showcase_index_plate_provenance_label()}
            value="MCMXXIII"
          />
          <Plate
            label={m.showcase_index_plate_hospitality_label()}
            value={m.showcase_index_plate_hospitality_value()}
          />
          <Plate
            label={m.showcase_index_plate_cuisine_label()}
            value={m.showcase_index_plate_cuisine_value()}
          />
        </div>
        <GoldRule variant="thin" />
      </div>
    </section>
  );
}

interface PlateProps {
  label: string;
  value: string;
}

function Plate({ label, value }: PlateProps) {
  return (
    <div>
      <div className="sc-eyebrow">{label}</div>
      <div
        className="mt-2 font-[family-name:var(--sc-font-display)] text-2xl text-[var(--sc-fg)]"
        style={{ letterSpacing: "var(--sc-tracking-display)" }}
      >
        {value}
      </div>
    </div>
  );
}

/* ─────────────────────────── SHARED SECTIONS ─────────────────────────── */

function PhotoStrip() {
  const { skin } = useSkin();
  return (
    <section className="mx-auto max-w-6xl px-6 pt-8 pb-12 sm:pt-10 sm:pb-14">
      <Link
        className="block focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--sc-accent)] focus-visible:outline-offset-2"
        to="/showcase/gallery"
      >
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3">
          {HERO_PHOTOS.map((p, i) => (
            <figure
              className={
                i === 2
                  ? "col-span-2 bg-[var(--sc-surface)] sm:col-span-1"
                  : "bg-[var(--sc-surface)]"
              }
              key={p.src}
              style={{
                border:
                  skin === "heritage"
                    ? "1px solid var(--sc-rule)"
                    : "1px solid var(--sc-border)",
                borderRadius: "var(--sc-radius)",
                overflow: "hidden",
                padding: skin === "heritage" ? "0.5rem" : 0,
              }}
            >
              <div
                className="sc-img-zoom sc-img-shine"
                style={{ borderRadius: "calc(var(--sc-radius) - 1px)" }}
              >
                <img
                  alt={p.alt}
                  className="sc-photo aspect-[4/3] w-full object-cover"
                  decoding="async"
                  height={600}
                  loading="lazy"
                  src={p.src}
                  width={800}
                />
              </div>
              {skin === "heritage" ? (
                <figcaption className="pt-2 pb-1 text-center font-[family-name:var(--sc-font-body)] text-[var(--sc-muted)] text-xs italic">
                  Plate {String.fromCharCode(73 + i)}
                </figcaption>
              ) : null}
            </figure>
          ))}
        </div>
        <p className="mt-4 text-right text-[var(--sc-accent)] text-sm">
          {m.showcase_index_view_gallery()}
          <ArrowRight
            aria-hidden="true"
            className="-mt-px ml-1 inline size-3.5"
          />
        </p>
      </Link>
    </section>
  );
}

function StorylineSection() {
  const { skin } = useSkin();
  const numerals =
    skin === "heritage"
      ? (["I", "II", "III"] as const)
      : (["01", "02", "03"] as const);
  return (
    <section className="mx-auto max-w-6xl px-6 pt-4 pb-12 sm:pt-6 sm:pb-14">
      <h2 className="font-[family-name:var(--sc-font-display)] text-3xl text-[var(--sc-fg)] sm:text-4xl">
        {m.showcase_index_storyline_title()}
      </h2>
      <div className="relative mt-12">
        {/* Continuous timeline thread linking the three steps (desktop only —
            steps stack on mobile). Sits at the numerals' vertical center
            (fontSize/2 under leading-none, robust across both skins) and is
            masked where each numeral overlaps it. */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-[24px] hidden h-px sm:top-[30px] sm:block"
          style={{ backgroundColor: "var(--sc-rule)" }}
        />
        <ol className="grid gap-x-6 gap-y-12 sm:grid-cols-3">
          <JourneyStep
            body={m.showcase_index_card_discover_body()}
            numeral={numerals[0]}
            title={m.showcase_index_card_discover_title()}
          />
          <JourneyStep
            body={m.showcase_index_card_calendar_body()}
            numeral={numerals[1]}
            title={m.showcase_nav_calendar()}
            to="/showcase/booking"
          />
          <JourneyStep
            body={m.showcase_index_card_concierge_body()}
            numeral={numerals[2]}
            title={m.showcase_nav_concierge()}
            to="/showcase/concierge"
          />
        </ol>
      </div>
    </section>
  );
}

function ReviewsTeaser() {
  return (
    <section className="mx-auto max-w-6xl px-6 pt-4 pb-16 sm:pt-6 sm:pb-20">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-3">
        <h2 className="font-[family-name:var(--sc-font-display)] text-3xl text-[var(--sc-fg)] sm:text-4xl">
          {m.showcase_index_reviews_title()}
        </h2>
        <Link
          className="inline-flex items-center gap-1 text-[var(--sc-accent)] text-sm hover:underline"
          to="/showcase/reviews"
        >
          <Star aria-hidden="true" className="size-3.5" />
          {m.showcase_index_view_all_reviews()}
        </Link>
      </div>
      <div
        className="border bg-[var(--sc-surface)] p-6"
        style={{
          borderColor: "var(--sc-border)",
          borderRadius: "var(--sc-radius)",
          boxShadow: "var(--sc-shadow)",
        }}
      >
        <ReviewsSummary />
      </div>
    </section>
  );
}

function WhySection() {
  return (
    <section className="mx-auto max-w-6xl px-6 pt-4 pb-12 sm:pt-6 sm:pb-16">
      <div className="grid gap-10 sm:grid-cols-12 sm:items-center sm:gap-12">
        <div className="sm:col-span-5">
          <h2 className="text-balance font-[family-name:var(--sc-font-display)] text-4xl text-[var(--sc-fg)] leading-[1.05] sm:text-5xl">
            {m.showcase_index_why_title()}
          </h2>
        </div>
        <ul className="sm:col-span-6 sm:col-start-7">
          <WhyPoint
            body={m.showcase_index_bullet1_body()}
            icon={Layers}
            title={m.showcase_index_bullet1_title()}
          />
          <WhyPoint
            body={m.showcase_index_bullet2_body()}
            icon={Palette}
            title={m.showcase_index_bullet2_title()}
          />
          <WhyPoint
            body={m.showcase_index_bullet3_body()}
            icon={ScrollText}
            title={m.showcase_index_bullet3_title()}
          />
        </ul>
      </div>
    </section>
  );
}

function ExploreSection() {
  return (
    <section className="mx-auto max-w-6xl px-6 pt-4 pb-20">
      <h2 className="mb-8 font-[family-name:var(--sc-font-display)] text-3xl text-[var(--sc-fg)] sm:text-4xl">
        {m.showcase_index_explore_title()}
      </h2>
      <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <ExploreLink
          body={m.showcase_index_explore_gallery_body()}
          icon={Camera}
          title={m.showcase_nav_gallery()}
          to="/showcase/gallery"
        />
        <ExploreLink
          body={m.showcase_index_explore_reviews_body()}
          icon={Star}
          title={m.showcase_nav_reviews()}
          to="/showcase/reviews"
        />
        <ExploreLink
          body={m.showcase_index_explore_operator_body()}
          icon={BarChart3}
          title={m.showcase_nav_operator()}
          to="/showcase/operator"
        />
        <ExploreLink
          body={m.showcase_index_explore_neighborhood_body()}
          icon={MapPin}
          title={m.showcase_nav_neighborhood()}
          to="/showcase/neighborhood"
        />
      </ul>
    </section>
  );
}

interface ExploreLinkProps {
  body: string;
  icon: LucideIcon;
  title: string;
  to:
    | "/showcase/gallery"
    | "/showcase/reviews"
    | "/showcase/operator"
    | "/showcase/neighborhood";
}

function ExploreLink({ to, title, body, icon: Icon }: ExploreLinkProps) {
  return (
    <li>
      <Link
        className="flex h-full flex-col border bg-[var(--sc-surface)] p-5 transition hover:bg-[var(--sc-surface-2)]"
        style={{
          borderColor: "var(--sc-border)",
          borderRadius: "var(--sc-radius)",
          boxShadow: "var(--sc-shadow)",
        }}
        to={to}
      >
        <Icon
          aria-hidden="true"
          className="mb-2 size-5 text-[var(--sc-accent)]"
        />
        <span className="mb-1 flex items-center gap-1 font-medium text-[var(--sc-fg)]">
          {title}
          <ArrowRight
            aria-hidden="true"
            className="size-3.5 text-[var(--sc-muted)]"
          />
        </span>
        <span className="text-[var(--sc-muted)] text-sm">{body}</span>
      </Link>
    </li>
  );
}

interface LandingTourCTAProps {
  accent?: "solid" | "invert";
}

function LandingTourCTA({ accent = "solid" }: LandingTourCTAProps) {
  const { active, completed, stepIndex, start, resume } = useTour();
  if (active) {
    return null;
  }
  const showResume = !completed && stepIndex > 0;
  const label = showResume
    ? m.showcase_tour_resume_tour()
    : m.showcase_index_take_tour_cta();

  const className =
    accent === "invert"
      ? "sc-btn inline-flex items-center gap-2 bg-white px-6 py-3 font-medium text-black shadow-sm transition hover:bg-white/90"
      : "sc-btn inline-flex items-center gap-2 bg-[var(--sc-fg)] px-6 py-3 font-medium text-[var(--sc-bg)] shadow-sm transition hover:opacity-90";

  return (
    <button
      className={className}
      onClick={showResume ? resume : start}
      type="button"
    >
      <PlayCircle aria-hidden="true" className="size-4" />
      {label}
    </button>
  );
}

interface JourneyStepProps {
  body: string;
  numeral: string;
  title: string;
  to?: "/showcase/booking" | "/showcase/concierge";
}

function JourneyStep({ numeral, title, body, to }: JourneyStepProps) {
  const content = (
    <>
      <div className="flex items-center gap-4">
        <span
          className="font-[family-name:var(--sc-font-display)] text-5xl text-[var(--sc-accent)] leading-none sm:relative sm:z-10 sm:bg-[var(--sc-bg)] sm:pr-3 sm:text-6xl"
          style={{ letterSpacing: "var(--sc-tracking-display)" }}
        >
          {numeral}
        </span>
        <span
          aria-hidden="true"
          className="h-px flex-1 sm:hidden"
          style={{ backgroundColor: "var(--sc-rule)" }}
        />
      </div>
      <h3 className="mt-6 font-[family-name:var(--sc-font-display)] text-2xl text-[var(--sc-fg)]">
        {title}
      </h3>
      <p className="mt-2 max-w-xs text-[var(--sc-muted)]">{body}</p>
      {to ? (
        <span className="mt-4 inline-flex text-[var(--sc-accent)] text-sm underline-offset-4 transition group-hover:underline">
          {to === "/showcase/booking"
            ? m.showcase_cta_try_calendar()
            : m.showcase_cta_try_concierge()}
        </span>
      ) : null}
    </>
  );
  return (
    <li className="group">
      {to ? (
        <Link className="block" to={to}>
          {content}
        </Link>
      ) : (
        content
      )}
    </li>
  );
}

interface WhyPointProps {
  body: string;
  icon: LucideIcon;
  title: string;
}

function WhyPoint({ title, body, icon: Icon }: WhyPointProps) {
  return (
    <li
      className="flex gap-4 border-t py-6 first:border-t-0 first:pt-0"
      style={{ borderColor: "var(--sc-rule)" }}
    >
      <Icon
        aria-hidden="true"
        className="mt-1 size-5 shrink-0 text-[var(--sc-accent)]"
      />
      <div>
        <h3 className="font-[family-name:var(--sc-font-display)] text-[var(--sc-fg)] text-xl">
          {title}
        </h3>
        <p className="mt-1 text-[var(--sc-muted)]">{body}</p>
      </div>
    </li>
  );
}
